import * as THREE from 'three';
import { getSmokeNoise } from '../smokeNoise';

export const CLOUD_PLANE = new THREE.PlaneGeometry(2, 2);
export const MAX_CLOUD_PTS = 16;

const SMOKE_VERT = /* glsl */ `
  varying vec2 vWorld;
  varying vec2 vUv;
  void main() {
    vUv = uv;
    vec4 w = modelMatrix * vec4(position, 1.0);
    vWorld = w.xy;
    gl_Position = projectionMatrix * viewMatrix * w;
  }
`;

const SMOKE_FRAG = /* glsl */ `
  uniform sampler2D uNoise;
  uniform vec3 uTint;
  uniform vec2 uOffset;
  uniform vec2 uSeed;
  uniform vec2 uAxis;
  uniform float uTime;
  uniform float uGain;
  uniform float uScale;
  uniform float uRadius;
  uniform float uStretch;
  uniform int uCount;
  uniform vec2 uPts[${MAX_CLOUD_PTS}];
  varying vec2 vWorld;
  varying vec2 vUv;

  void main() {
    vec2 uv = vWorld * uScale * 0.04 + uOffset + uSeed;
    vec2 q = texture2D(uNoise, uv * 0.34 + vec2(uTime * 0.006, -uTime * 0.004)).rg;
    vec2 flow = q - 0.5;
    vec2 curl = vec2(flow.y, -flow.x);
    float cs = cos(uTime * 0.22);
    float sn = sin(uTime * 0.22);
    vec2 roll = curl * sn + flow * cs;
    float f = texture2D(uNoise, uv * 0.62 + roll * 0.4).r;
    vec2 drifted = vWorld + roll * 3.5;
    float invR2 = 1.0 / (max(uRadius, 0.4) * max(uRadius, 0.4));
    float wispGain = mix(0.2, 0.85, f);
    vec2 mid = vec2(0.0);
    for (int i = 0; i < ${MAX_CLOUD_PTS}; i++) {
      if (i >= uCount) break;
      mid += uPts[i];
    }
    mid /= max(float(uCount), 1.0);
    float field = 0.0;
    for (int i = 0; i < ${MAX_CLOUD_PTS}; i++) {
      if (i >= uCount) break;
      vec2 d = drifted - uPts[i];
      field += exp(-dot(d, d) * invR2);
      vec2 wispA = uPts[i] - mid + roll * 0.7;
      float wLen = max(length(wispA), 0.001);
      wispA /= wLen;
      vec2 wispP = vec2(-wispA.y, wispA.x);
      float wa = dot(d, wispA);
      float wp = dot(d, wispP);
      field += exp(-(wa * wa * 0.18 + wp * wp * 3.6) * invR2) * wispGain;
    }
    float fall = 1.0 - exp(-min(field, 1.5) * 0.34);
    vec2 p = vUv * 2.0 - 1.0;
    float box = 1.0 - smoothstep(0.52, 1.0, length(p));
    float torn = mix(0.28, 1.0, f);
    float a = fall * torn * box * (0.08 + f * 0.2) * uGain;
    vec3 col = mix(uTint * vec3(0.34, 0.38, 0.5), uTint * 0.68, q.g);
    gl_FragColor = vec4(col, a);
  }
`;

export const SMOKE_LAYERS = [
  { zOff: -1.8, zT: 0.08, sizeMul: 1.12, gain: 0.16, scale: 0.48, timeScale: 0.8, order: -32, offset: [0.12, 0.28], radius: 4.2 },
  { zOff: -3.2, zT: 0.52, sizeMul: 1.24, gain: 0.09, scale: 0.4, timeScale: 0.58, order: -34, offset: [0.28, 0.06], radius: 4.8, nestOnly: true },
  { zOff: -4.6, zT: 1, sizeMul: 1.4, gain: 0.07, scale: 0.34, timeScale: 0.46, order: -36, offset: [0.04, 0.4], radius: 5.4, nestOnly: true },
];

export function makeSmokeMaterial(tint, layer) {
  const pts = Array.from({ length: MAX_CLOUD_PTS }, () => new THREE.Vector2());
  return new THREE.ShaderMaterial({
    uniforms: {
      uNoise: { value: getSmokeNoise() },
      uTint: { value: new THREE.Color(tint) },
      uOffset: { value: new THREE.Vector2(layer.offset[0], layer.offset[1]) },
      uSeed: { value: new THREE.Vector2() },
      uAxis: { value: new THREE.Vector2(1, 0) },
      uTime: { value: 0 },
      uGain: { value: layer.gain },
      uScale: { value: layer.scale },
      uRadius: { value: layer.radius },
      uStretch: { value: 1.15 },
      uCount: { value: 0 },
      uPts: { value: pts },
    },
    transparent: true,
    depthWrite: false,
    depthTest: false,
    blending: THREE.NormalBlending,
    fog: false,
    toneMapped: false,
    vertexShader: SMOKE_VERT,
    fragmentShader: SMOKE_FRAG,
  });
}
